import type { Messages } from "../catalogue.en";

/**
 * Clinical Summary: the patient card's AI summary — its build act, version
 * strip, Word download and the clinic letterhead it is laid out on.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const clinicalSummary: Partial<Messages> = {
  "patient.summary.title": "סיכום קליני",
  "patient.summary.description": "נוצר באמצעות בינה מלאכותית מנתונים קליניים בתיק וממסמכי המטופל.",
  "patient.summary.build": "יצירת סיכום קליני",
  "patient.summary.update": "עדכון הסיכום",
  "patient.summary.full": "יצירה מחדש",
  "patient.summary.history": "היסטוריה",
  "patient.summary.language": "שפת הסיכום",
  "patient.summary.cancel": "ביטול היצירה",
  "patient.summary.empty": "טרם נוצר סיכום קליני.",
  "patient.summary.busy": "מקטעי מקור שעובדו: {done} מתוך {total}",
  "patient.summary.failed": "היצירה נכשלה. הסיכום הקודם נשמר.",
  "patient.summary.cancelled": "היצירה בוטלה. הסיכום הקודם נשמר.",
  "patient.summary.showEmpty": "הצגת סעיפים ריקים ({count})",
  "patient.summary.sources": "מקורות",
  "patient.summary.modeFull": "נבנה מההתחלה",
  "patient.summary.modeUpdate": "עודכן בנתונים חדשים",
  "patient.summary.modeCoverage": "רשימת המקורות עודכנה",
  "patient.summary.modeUnchanged": "ללא שינויים",
  "patient.summary.more": "טעינת גרסאות מוקדמות יותר",
  "patient.summary.readDenied": "אין הרשאה לכל המקורות הדרושים לסיכום הקליני.",
  "patient.summary.requestFailed": "לא ניתן להשלים את הפעולה. יש לנסות שוב.",
  "patient.summary.warnings": "הערות הבינה המלאכותית על הכנת הסיכום",
  "patient.summary.menu": "פעולות בסיכום הקליני",
  "patient.summary.source": "פתיחה",
  "patient.summary.noSources": "אין נתונים קליניים או טקסט מסמכים המתאימים לסיכום.",
  "patient.summary.unknownModel": "למודל שנבחר אין פרופיל יכולות מאומת. יש לבחור מודל נתמך בהגדרות הבינה המלאכותית.",
  "patient.summary.limit": "היצירה הגיעה למגבלת עיבוד. הסיכום הקודם נשמר.",
  "patient.summary.latestLanguages": "הגרסאות האחרונות לפי שפה",
  "patient.summary.languageResourcesInvalid": "משאבי השפה של הסיכום חסרים או אינם תקינים. יש לפנות למנהל המערכת לבדיקת קובצי העזר של הבינה המלאכותית.",
  "patient.summary.busyTitle": "הסיכום הקליני נוצר…",
  "patient.summary.emptyDetail": "יש לבדוק את המקורות ולהפעיל את היצירה: הבינה המלאכותית תרכיב את הסיכום מנתוני התיק והמסמכים שנבחרו; כל גרסה שומרת את מקורותיה.",
  "patient.summary.stateLatest": "הגרסה הנוכחית",
  "patient.summary.stateHistorical": "גרסה היסטורית",
  "patient.summary.backToLatest": "חזרה לגרסה הנוכחית",
  "patient.summary.changedRetry": "המקורות או הבחירה השתנו בזמן התחלת היצירה. יש לטעון את ההרכב הנוכחי, לבדוק אותו ולנסות שוב.",
  "patient.summary.aiProjectUnavailable": "לארגון עדיין לא הוגדר פרויקט בינה מלאכותית. יש לפנות למנהל המערכת להשלמת הגדרת הבינה המלאכותית של הארגון.",
  "patient.summary.keyMissing": "יש להוסיף את מפתח OpenAI שלך בהגדרות החשבון לפני יצירת סיכום.",
  "patient.summary.functionDisabled": "יצירת סיכום קליני מושבתת למרפאה זו במרכז הבקרה.",
  "patient.summary.serviceUnavailable": "שירות הבינה המלאכותית אינו זמין כעת. יש לנסות שוב מאוחר יותר.",
  "patient.summary.generateDenied": "אין לך הרשאה להתחיל יצירת סיכום.",
  "patient.summary.download": "הורדה כקובץ Word",
  "patient.summary.downloadFailed": "לא ניתן היה להכין את מסמך ה-Word. יש לנסות שוב.",
  "patient.summary.downloadLetterhead": "לא ניתן לקרוא את נייר המכתבים של המרפאה. יש להחליף או להסיר אותו בתפריט הסיכום.",
  "patient.summary.downloadUnavailable": "שירות המסמכים אינו זמין כעת. יש לנסות שוב מאוחר יותר.",
  "patient.summary.letterhead": "נייר המכתבים של המרפאה",
  "patient.summary.letterheadLoaded": "נטען: {name}. משמש במסמכי ה-Word של כל המטופלים.",
  "patient.summary.letterheadNone": "לא נטען. במסמכי Word מופיעה כותרת רגילה עם פרטי הארגון.",
  "patient.summary.letterheadUpload": "העלאת נייר מכתבים (DOCX)",
  "patient.summary.letterheadReplace": "החלפת נייר המכתבים",
  "patient.summary.letterheadRemove": "הסרת נייר המכתבים",
  "patient.summary.letterheadPicker": "קובץ נייר המכתבים",
  "patient.summary.letterheadRemoveTitle": "להסיר את נייר המכתבים של המרפאה?",
  "patient.summary.letterheadRemoveDescription": "מסמכי ה-Word של כל המטופלים יופקו עם כותרת רגילה ובה פרטי הארגון.",
  "patient.summary.letterheadRemoveConfirm": "הסרה",
  "patient.summary.letterheadKeep": "השארה",
  "patient.summary.letterheadFailed": "לא ניתן היה לשמור את נייר המכתבים. יש לנסות שוב.",
  "patient.summary.letterheadInvalid": "הקובץ אינו מסמך Word (DOCX).",
  "patient.summary.letterheadActive": "נייר המכתבים מכיל פקודות מאקרו, אובייקטים מוטמעים או תוכן חיצוני. יש לשמור אותו כקובץ DOCX רגיל ולהעלות שוב.",
  "patient.summary.letterheadTooLarge": "קובץ נייר המכתבים לא יכול לעלות על 4 MB.",
  "patient.summary.letterheadChanged": "נייר המכתבים שונה זה עתה על ידי משתמש אחר. מוצג המצב הנוכחי; יש לנסות שוב.",
  "patient.summary.letterheadQuota": "האחסון של הארגון מלא; נייר המכתבים לא נשמר.",
  "patient.summary.letterheadDenied": "אין לך הרשאה לשנות את נייר המכתבים של המרפאה.",
  "patient.summary.compose": "בחירת מקורות",
  "patient.summary.composeShared": "הבחירה חלה על הסיכום של מטופל זה אצל כל הרופאים ובכל שפות הסיכום. מקורות שלא סומנו אינם נשלחים לבינה המלאכותית.",
  "patient.summary.saveChoices": "שמירת הבחירה",
  "patient.summary.saveAndBuild": "שמירה ובנייה",
  "patient.summary.saveAndUpdate": "שמירה ועדכון",
  "patient.summary.starting": "מתחיל…",
  "patient.summary.choicesSaved": "הבחירה נשמרה. לא הופעלה יצירה.",
  "patient.summary.notStarted": "היצירה לא הופעלה; הבחירה השמורה נשארת בתוקף.",
  "patient.summary.composeWhileRunning": "היצירה מתבצעת כעת. אפשר לשמור את הבחירה עכשיו — היא תחול בבנייה הבאה.",
  "patient.summary.excludedChanged": "השתנה לאחר שהוחרג",
  "patient.summary.reviewNeeded": "מקורות שהוחרגו השתנו",
  "patient.summary.reviewNeededDetail": "יש לבדוק את בחירת המקורות. השמירה מאשרת את ההחרגות כפי שהן מוצגות ואינה מפעילה יצירה.",
  "patient.summary.reviewSources": "בדיקת המקורות",
  "patient.summary.choicesChanged": "הבחירה או המקורות השתנו מאז הצגת הרשימה. יש לטעון את ההרכב הנוכחי ולבדוק אותו; השינויים שלא נשמרו נשארים עד הטעינה.",
  "patient.summary.loadCurrent": "טעינת ההרכב הנוכחי",
  "patient.summary.reloaded": "מוצג ההרכב הנוכחי; יש לבדוק אותו שוב.",
  "patient.summary.saveChoicesDenied": "אין לך הרשאה לשנות את בחירת המקורות.",
  "patient.summary.unsavedChoices": "בחירת מקורות לסיכום הקליני שלא נשמרה",
  "patient.summary.sourcesCount": "מקורות · {count}",
  "patient.summary.untitled": "ללא שם",
  "patient.summary.kindChart": "דף כותרת ואנמנזה",
  "patient.summary.kindRecords": "רשומות רפואיות",
  "patient.summary.kindDocuments": "מסמכים",
  "patient.summary.kindLaboratory": "תוצאות מעבדה",
  "patient.summary.kindVitals": "סימנים חיוניים",
  "patient.summary.kindReferrals": "הפניות",
  "patient.summary.kindStays": "אשפוזים",
  "patient.summary.kindOther": "אחר",
  "patient.summary.kindCount": "{kind}: {count}",
  "patient.summary.measurement": "מדידה",
  "patient.summary.measurementValues": "ערכים: {count}",
  "patient.summary.reasonGone": "כבר לא זמין",
  "patient.summary.composeTotal": "ייכללו: {included} מתוך {total}",
  "patient.summary.groupCount": "{included} מתוך {total}",
  "patient.summary.groupTick": "לכלול: {group}",
  "patient.summary.versionUsed": "מקורות שנכללו: {count}",
  "patient.summary.versionContents": "הרכב",
  "patient.summary.period": "נתונים מ-{from} עד {to}",
  "patient.summary.periodDay": "נתונים מיום {date}",
  "patient.summary.changedTitle": "התיק השתנה מאז שגרסה זו נבנתה",
  "patient.summary.changedAdded": "מקורות חדשים: {count}",
  "patient.summary.changedEdited": "השתנו: {count}",
  "patient.summary.changedRemoved": "כבר אינם בשימוש: {count}",
  "patient.summary.reviewNeededInline": "מקורות שהוחרגו השתנו — יש לבדוק את הבחירה בעת העדכון.",
};
